import React, { useState } from 'react';
import {
    Badge,
    Button,
    Card,
    Col,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    ApiOutlined,
    SendOutlined,
    SearchOutlined,
    StopOutlined,
    SyncOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { ESSProductSummary, useDecommissionProduct, useESSProducts, useSendProductDetail } from '../hooks/useESSProducts';

const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_COLORS: Record<string, string> = {
    SYNCED: 'success',
    DECOMMISSIONED: 'default',
    PENDING: 'processing',
    FAILED: 'error',
};

const ESSProductManagementView: React.FC = () => {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [search, setSearch] = useState('');
    const [decommissionTarget, setDecommissionTarget] = useState<ESSProductSummary | null>(null);
    const [reason, setReason] = useState('');

    const { data, isLoading, isError, refetch } = useESSProducts({ page, size: pageSize, status: statusFilter });
    const decommission = useDecommissionProduct();
    const sendDetail = useSendProductDetail();

    const filteredContent = (data?.data?.content ?? []).filter(p =>
        !search ||
        p.productName?.toLowerCase().includes(search.toLowerCase()) ||
        p.productCode?.toLowerCase().includes(search.toLowerCase()) ||
        p.deductionCode?.toLowerCase().includes(search.toLowerCase())
    );

    const handleDecommission = () => {
        if (!decommissionTarget) return;
        decommission.mutate(
            { glNo: decommissionTarget.glNo, reason },
            {
                onSuccess: (res) => {
                    if (res.data?.success) {
                        message.success(`Product "${decommissionTarget.productName}" decommissioned from ESS`);
                        setDecommissionTarget(null);
                        setReason('');
                    } else {
                        message.error(res.data?.message ?? 'Decommission failed');
                    }
                },
                onError: (err: any) => {
                    message.error(err?.response?.data?.message ?? 'Failed to decommission product');
                },
            }
        );
    };

    const columns = [
        {
            title: 'Product Name',
            dataIndex: 'productName',
            key: 'productName',
            sorter: (a: ESSProductSummary, b: ESSProductSummary) =>
                (a.productName ?? '').localeCompare(b.productName ?? ''),
        },
        {
            title: 'Product Code',
            dataIndex: 'productCode',
            key: 'productCode',
        },
        {
            title: 'Deduction Code',
            dataIndex: 'deductionCode',
            key: 'deductionCode',
            render: (v: string) => v ?? <Text type="secondary">—</Text>,
        },
        {
            title: 'ESS Status',
            dataIndex: 'emkopoSyncStatus',
            key: 'emkopoSyncStatus',
            render: (status: string) => (
                <Tag color={STATUS_COLORS[status] ?? 'default'}>{status}</Tag>
            ),
        },
        {
            title: 'Last Sync',
            dataIndex: 'emkopoLastSyncDate',
            key: 'emkopoLastSyncDate',
            render: (v: string | null) =>
                v ? new Date(v).toLocaleString() : <Text type="secondary">—</Text>,
        },
        {
            title: 'Active',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (v: boolean) =>
                v ? <Badge status="success" text="Yes" /> : <Badge status="default" text="No" />,
        },
        {
            title: 'Last Error',
            dataIndex: 'emkopoLastSyncError',
            key: 'emkopoLastSyncError',
            render: (v: string | null) =>
                v ? (
                    <Tooltip title={v}>
                        <Text type="danger" ellipsis style={{ maxWidth: 200 }}>
                            <WarningOutlined /> {v}
                        </Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: ESSProductSummary) => (
                <Space size="small">
                    <Tooltip
                        title={
                            record.emkopoSyncStatus === 'DECOMMISSIONED'
                                ? 'Send to reactivate product in ESS'
                                : 'Send PRODUCT_DETAIL to ESS on demand'
                        }
                    >
                        <Button
                            size="small"
                            icon={<SendOutlined />}
                            loading={sendDetail.isPending && sendDetail.variables === record.glNo}
                            onClick={() => {
                                sendDetail.mutate(record.glNo, {
                                    onSuccess: (res) => {
                                        if (res.data?.success) {
                                            message.success(`PRODUCT_DETAIL sent for "${record.productName}"`);
                                        } else {
                                            message.warning(res.data?.message ?? 'Send returned failure');
                                        }
                                    },
                                    onError: (err: any) => {
                                        message.error(err?.response?.data?.message ?? 'Failed to send PRODUCT_DETAIL');
                                    },
                                });
                            }}
                        >
                            Send Detail
                        </Button>
                    </Tooltip>
                    <Tooltip
                        title={
                            record.emkopoSyncStatus === 'DECOMMISSIONED'
                                ? 'Already decommissioned'
                                : 'Decommission from ESS'
                        }
                    >
                        <Button
                            danger
                            size="small"
                            icon={<StopOutlined />}
                            disabled={record.emkopoSyncStatus !== 'SYNCED'}
                            onClick={() => {
                                setDecommissionTarget(record);
                                setReason('');
                            }}
                        >
                            Decommission
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Title level={4}>
                        <ApiOutlined style={{ marginRight: 8 }} />
                        ESS Product Management
                    </Title>
                    <Text type="secondary">
                        Manage loan products that have been synced to ESS. Decommission products to remove them from e-Mkopo.
                    </Text>
                </Col>
                <Col>
                    <Tooltip title="Refresh">
                        <Button icon={<SyncOutlined />} onClick={() => refetch()} loading={isLoading} />
                    </Tooltip>
                </Col>
            </Row>

            <Row gutter={12} style={{ marginBottom: 16 }}>
                <Col>
                    <Input
                        placeholder="Search by name / code / deduction code"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        allowClear
                        style={{ width: 300 }}
                    />
                </Col>
                <Col>
                    <Select
                        placeholder="Filter by status"
                        allowClear
                        style={{ width: 180 }}
                        value={statusFilter}
                        onChange={v => { setStatusFilter(v); setPage(0); }}
                    >
                        <Option value="SYNCED">Synced</Option>
                        <Option value="DECOMMISSIONED">Decommissioned</Option>
                        <Option value="PENDING">Pending</Option>
                        <Option value="FAILED">Failed</Option>
                    </Select>
                </Col>
            </Row>

            {isError && (
                <Text type="danger">Failed to load ESS products. Please try again.</Text>
            )}

            <Table
                columns={columns}
                dataSource={filteredContent}
                rowKey="glNo"
                loading={isLoading}
                pagination={{
                    current: page + 1,
                    pageSize,
                    total: data?.data?.totalElements ?? 0,
                    onChange: (p, ps) => { setPage(p - 1); setPageSize(ps); },
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} products`,
                }}
                rowClassName={(record) =>
                    record.emkopoSyncStatus === 'DECOMMISSIONED' ? 'ant-table-row-disabled' : ''
                }
            />

            <Modal
                title={
                    <Space>
                        <StopOutlined style={{ color: '#ff4d4f' }} />
                        Decommission Product from ESS
                    </Space>
                }
                open={!!decommissionTarget}
                onCancel={() => { setDecommissionTarget(null); setReason(''); }}
                onOk={handleDecommission}
                okText="Decommission"
                okButtonProps={{ danger: true, loading: decommission.isPending }}
                cancelButtonProps={{ disabled: decommission.isPending }}
            >
                {decommissionTarget && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text>
                            You are about to decommission{' '}
                            <Text strong>"{decommissionTarget.productName}"</Text> (code:{' '}
                            <Text code>{decommissionTarget.productCode}</Text>, deduction code:{' '}
                            <Text code>{decommissionTarget.deductionCode ?? '—'}</Text>
                            ) from ESS.
                        </Text>
                        <Text type="warning">
                            <WarningOutlined /> This will send a PRODUCT_DECOMMISSION message to e-Mkopo / ESS. This action cannot be undone from this portal.
                        </Text>
                        <Input.TextArea
                            rows={3}
                            placeholder="Reason for decommission (optional)"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            maxLength={255}
                            showCount
                        />
                    </Space>
                )}
            </Modal>
        </Card>
    );
};

export default ESSProductManagementView;
